import { useEffect, useState } from 'react';
import { Icon } from '../../../layout/Icon.jsx';
import { apiError } from '../../../shared/api.js';
import { formatIQD, formatDateTime } from '../../../shared/format.js';
import { toast } from '../../../shared/useToast.js';
import { useCancelInvoice } from '../hooks/useSales.js';

const METHOD_LABEL = {
  cash: 'نقدي',
  card: 'بطاقة',
  transfer: 'تحويل',
  card_balance: 'رصيد الكارت',
  on_session: 'تعليق على الجلسة',
  credit: 'آجل',
  external: 'خارجي'
};

/**
 * نافذة تأكيد إلغاء فاتورة.
 *
 * تأكيد داخل التطبيق لا `window.confirm`: نافذة المتصفح تُحجب صامتة في
 * بعض الأُطر فلا يُرسل الطلب أبداً (نفس سبب نافذة حذف البطاقات).
 *
 * السبب مطلوب هنا في الواجهة (لا في الخادم) لأن تقرير «الفواتير الملغاة»
 * يعرضه، ولأن الإلغاء إجراء لا رجعة فيه. الخادم يقبل الإلغاء بلا سبب حفاظاً
 * على توافق النداءات القائمة.
 */
export function CancelInvoiceModal({ invoice, onClose, onCancelled }) {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const cancelM = useCancelInvoice();

  useEffect(() => {
    setReason('');
    setError('');
  }, [invoice && (invoice._id || invoice.id)]);

  if (!invoice) return null;

  const id = invoice._id || invoice.id;
  const alreadyCancelled = invoice.paymentStatus === 'cancelled';
  const paid = Number(invoice.paidAmount || 0);
  const reasonOk = reason.trim().length >= 3;

  async function submit() {
    setError('');
    if (!reasonOk) {
      setError('اكتب سبب الإلغاء (3 أحرف على الأقل)');
      return;
    }
    try {
      const result = await cancelM.mutateAsync({ id, reason: reason.trim() });
      toast.success(`تم إلغاء الفاتورة ${invoice.number} — بقيت محفوظة بحالة «ملغاة»`);
      onCancelled?.(result);
      onClose?.();
    } catch (err) {
      // سبب الرفض يبقى معروضاً داخل النافذة (شفت مغلق، رصيد الدرج لا يكفي،
      // الفاتورة ملغاة أصلاً) بدل أن يختفي مع التوست.
      setError(apiError(err));
    }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" style={{ background: 'rgba(15,16,18,0.55)' }}>
      <div className="card w-[480px] max-w-full space-y-0 p-0" onClick={(e) => e.stopPropagation()}>
        <div className="card-h">
          <div className="flex-1">
            <h3 className="text-[15px] font-bold m-0">إلغاء الفاتورة</h3>
            <div className="text-xs text-ink-3 mt-0.5">فاتورة #{invoice.number}</div>
          </div>
          <button type="button" className="text-ink-3 hover:text-ink" onClick={onClose}>
            <Icon name="x" size={16} />
          </button>
        </div>

        <div className="card-b space-y-3 text-[13px]">
          {alreadyCancelled ? (
            <div className="text-[12.5px] text-red font-semibold border border-red/30 bg-red/5 rounded-[8px] p-2.5">
              هذه الفاتورة ملغاة مسبقاً — لا يمكن إلغاؤها مرة ثانية.
            </div>
          ) : null}

          <div className="rounded-[10px] bg-paper-2 p-2.5 space-y-1 text-[12.5px]">
            <Row label="التاريخ" value={formatDateTime(invoice.createdAt)} />
            <Row label="الإجمالي" value={formatIQD(invoice.grandTotal || 0)} bold />
            <Row label="المدفوع" value={formatIQD(paid)} />
            <Row label="طريقة الدفع" value={METHOD_LABEL[invoice.paymentMethod] || invoice.paymentMethod || '—'} />
          </div>

          <div className="rounded-[10px] p-2.5 text-[12px] leading-relaxed" style={{ background: '#FFF7ED', color: '#92400E' }}>
            <div className="font-bold mb-1">ماذا سيحدث؟</div>
            <ul className="list-disc ps-4 space-y-0.5">
              <li>تبقى الفاتورة محفوظة في القاعدة بحالة «ملغاة» — لا تُحذف أبداً.</li>
              <li>لا تُحتسب بعدها في الإيرادات ولا في المبيعات ولا في ديون العملاء.</li>
              <li>تُرجَّع كميات الأصناف إلى المخزون مرة واحدة.</li>
              {invoice.paymentMethod === 'cash' && paid > 0 ? (
                <li>يخرج المبلغ المُعاد {formatIQD(paid)} من درج شفتك المفتوح (يتطلب شفتاً وسيولة كافية).</li>
              ) : null}
              {invoice.paymentMethod === 'card_balance' && paid > 0 ? (
                <li>يعود المبلغ {formatIQD(paid)} إلى رصيد الكارت.</li>
              ) : null}
              <li>يُسجَّل الإلغاء باسمك وتاريخه وسببه في سجلّ التدقيق وتقرير الفواتير الملغاة.</li>
            </ul>
          </div>

          <div>
            <label className="block text-xs font-semibold text-ink-2 mb-1">
              سبب الإلغاء <span className="text-red">*</span>
            </label>
            <textarea
              className="input"
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="مثلاً: الزبون تراجع عن الطلب / خطأ في إدخال الصنف"
              disabled={alreadyCancelled || cancelM.isPending}
            />
          </div>

          {error ? (
            <div className="text-[12.5px] text-red font-semibold border border-red/30 bg-red/5 rounded-[8px] p-2.5">
              {error}
            </div>
          ) : null}
        </div>

        <div className="card-h border-b-0 border-t flex gap-2">
          <button
            type="button"
            className="btn flex-1 justify-center"
            onClick={onClose}
            disabled={cancelM.isPending}
          >
            تراجع
          </button>
          <button
            type="button"
            className="btn btn-danger flex-1 justify-center"
            onClick={submit}
            disabled={alreadyCancelled || cancelM.isPending || !reasonOk}
          >
            <Icon name="trash" size={14} />
            {cancelM.isPending ? 'جاري الإلغاء...' : 'تأكيد الإلغاء'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-ink-3">{label}</span>
      <span className={bold ? 'font-extrabold' : 'font-semibold'}>{value}</span>
    </div>
  );
}
